import { LoansPaths } from 'app/configs/routesConfig/loans';
import _ from 'lodash';

import { OpenLoanSectionArea, ToolExecutionStatus } from 'constants/aiChat';
import { LoanApplicationTabRouteParam } from 'constants/loanApplication';

import {
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_ACTION_TYPE,
  FINANCIAL_INFO_VIEWS_PATHS,
} from '../../financialInfo/FinancialInfo.constants';

import {
  handleNavigateToBorrower,
  handleNavigateToField,
  handleNavigateToFinancialView,
  handleNavigateToTab,
  navigateToPath,
} from './navigationToolHandler.utils';

const AREA_TO_FINANCIAL_VIEW = {
  [OpenLoanSectionArea.INCOMES]: FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT,
  [OpenLoanSectionArea.ASSETS]: FINANCIAL_INFO_VIEWS_PATHS.ASSETS,
  [OpenLoanSectionArea.LIABILITIES]: FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES,
  [OpenLoanSectionArea.PROPERTIES]: FINANCIAL_INFO_VIEWS_PATHS.REO,
};

const AREA_TO_TAB = {
  [OpenLoanSectionArea.FINANCIAL_VIEW]: 'financial-information',
  [OpenLoanSectionArea.TASKS]: 'tasks',
  [OpenLoanSectionArea.DOCUMENTS]: 'documents',
  [OpenLoanSectionArea.CLOSING]: 'closing-new',
};

const navigateToFinancialEntityDrawer = ({ context, viewName, borrowerId, entityId }) => {
  const { history, applicationId } = context;

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: LoanApplicationTabRouteParam.FINANCIAL_INFO,
    subtab: viewName,
    entityId: borrowerId || ALL_BORROWERS_PATH,
    section: FINANCIAL_INFO_ACTION_TYPE.EDIT,
    subsection: entityId,
  });

  if (!navigateToPath(history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: { navigatedTo: path, viewName, borrowerId, entityId },
  };
};

const navigateToFinancialArea = ({ area, context, borrowerId, entityId }) => {
  const viewName = AREA_TO_FINANCIAL_VIEW[area];

  if (!_.isNil(entityId)) {
    return navigateToFinancialEntityDrawer({ context, viewName, borrowerId, entityId });
  }

  return handleNavigateToFinancialView({
    parameters: { viewName, borrowerId },
    context,
  });
};

const navigateToTabArea = ({ area, context }) =>
  handleNavigateToTab({ parameters: { tabName: AREA_TO_TAB[area] }, context });

const navigateToBorrowerArea = ({ context, borrowerId }) => {
  if (_.isNil(borrowerId)) {
    return handleNavigateToTab({
      parameters: { tabName: 'borrowers' },
      context,
    });
  }

  return handleNavigateToBorrower({
    parameters: { borrowerId },
    context,
  });
};

const navigateToFieldArea = ({ context, fieldId, borrowerId }) => {
  if (_.isNil(fieldId)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'fieldId is required when area is "field"',
    };
  }

  return handleNavigateToField({ parameters: { fieldId, borrowerId }, context });
};

const dispatchByArea = ({ area, parameters, context }) => {
  const { borrowerId, fieldId, entityId } = parameters;

  if (_.isEqual(area, OpenLoanSectionArea.FIELD)) {
    return navigateToFieldArea({ context, fieldId, borrowerId });
  }

  if (_.isEqual(area, OpenLoanSectionArea.BORROWER)) {
    return navigateToBorrowerArea({ context, borrowerId });
  }

  if (!_.isNil(AREA_TO_FINANCIAL_VIEW[area])) {
    return navigateToFinancialArea({ area, context, borrowerId, entityId });
  }

  if (!_.isNil(AREA_TO_TAB[area])) {
    return navigateToTabArea({ area, context });
  }

  return {
    success: false,
    status: ToolExecutionStatus.FAILED,
    error: `Unknown open_loan_section area: ${area}`,
  };
};

const fallbackToLoanRoot = ({ context }) => {
  const { history, applicationId } = context;

  const path = LoansPaths.applicationManagement.generatePath({
    applicationId,
    tab: LoanApplicationTabRouteParam.PROPERTY_AND_LOAN,
  });

  if (!navigateToPath(history, path)) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  return {
    success: true,
    status: ToolExecutionStatus.COMPLETED,
    result: { navigatedTo: path },
  };
};

export const openLoanSectionToolHandler = async ({ parameters, context }) => {
  const { history, applicationId } = context;

  if (!history) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Navigation context not available',
    };
  }

  if (!applicationId) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: 'Application ID is required for navigation',
    };
  }

  const { area } = parameters;

  if (!area) {
    return fallbackToLoanRoot({ context });
  }

  try {
    return await dispatchByArea({ area, parameters, context });
  } catch (error) {
    return {
      success: false,
      status: ToolExecutionStatus.FAILED,
      error: error.message || 'Navigation failed',
    };
  }
};

export default openLoanSectionToolHandler;
